"use client";

import React, { useCallback } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal, Loader2 } from "lucide-react";
import { TooltipWithContent } from "@/components/ui/tooltip";
import { getIconComponent } from "./iconUtils";
import { groupActionsByCategory } from "@/lib/actions/action-categories";
import type { DetailAction, BaseEntity } from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { ActionGroups } from "../../hooks/useTableRowActions";

// ============================================================================
// TYPES
// ============================================================================

interface DesktopActionsViewProps<T extends BaseEntity> {
  item: T;
  visibleActions: DetailAction<T>[];
  actionGroups: ActionGroups<T>;
  loadingActions: Set<string>;
  onAction: (action: DetailAction<T>) => void;
  compact?: boolean;
  className?: string;
}

// ============================================================================
// HELPERS
// ============================================================================

type ButtonVariant = "default" | "destructive" | "outline" | "secondary" | "ghost" | "link";

function getButtonVariant(variant?: string): ButtonVariant {
  switch (variant) {
    case "success":
      return "default";
    case "destructive":
      return "destructive";
    case "outline":
      return "outline";
    case "secondary":
      return "secondary";
    default:
      return "ghost";
  }
}

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * Desktop layout: primary actions as icon buttons, overflow in a "More" dropdown.
 */
export function DesktopActionsView<T extends BaseEntity>({
  item,
  visibleActions,
  actionGroups,
  loadingActions,
  onAction,
  compact = false,
  className = "",
}: DesktopActionsViewProps<T>) {
  const renderActionButton = useCallback(
    (action: DetailAction<T>, isDropdown = false) => {
      const isLoading = loadingActions.has(action.id);
      const isDisabled =
        action.disabled && typeof action.disabled === "function" && action.disabled(item);
      const label = typeof action.label === "function" ? action.label(item) : action.label;

      // Compact mode: short text-label pill for actions that opt in
      // (e.g. Ask Meridian — its icon doesn't render at icon size).
      if (compact && !isDropdown && action.showLabelInRow) {
        return (
          <Button
            key={action.id}
            variant={getButtonVariant(action.variant)}
            size="sm"
            disabled={isDisabled || isLoading}
            onClick={() => onAction(action)}
            className={`h-7 px-2 text-xs ${action.className || ""}`}
            title={label}
          >
            {isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : label}
          </Button>
        );
      }

      // Compact mode: icon-only with tooltip
      if (compact && !isDropdown) {
        const IconComponent = action.icon ? getIconComponent(action.icon) : null;
        return (
          <TooltipWithContent content={label} key={action.id}>
            <Button
              variant="ghost"
              size="sm"
              disabled={isDisabled || isLoading}
              onClick={() => onAction(action)}
              className="h-7 w-7 p-0 hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-center"
              aria-label={label}
              title={label}
            >
              {isLoading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : IconComponent ? (
                React.createElement(IconComponent, { className: "h-3.5 w-3.5" })
              ) : (
                label
              )}
            </Button>
          </TooltipWithContent>
        );
      }

      // Standard mode: button with label
      return (
        <Button
          key={action.id}
          variant={getButtonVariant(action.variant)}
          size="sm"
          disabled={isDisabled || isLoading}
          onClick={() => onAction(action)}
          className={`${action.className || ""} ${isDropdown ? "w-full justify-start" : ""}`}
        >
          {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : label}
        </Button>
      );
    },
    [item, loadingActions, onAction, compact]
  );

  // One overflow-menu row. Shared by every category group so the dropdown
  // stays consistent with the right-click menu (RowContextMenuContent).
  const renderSecondaryItem = useCallback(
    (action: DetailAction<T>) => {
      const label =
        typeof action.label === "function" ? action.label(item) : action.label;
      const isDisabled =
        action.disabled && typeof action.disabled === "function" && action.disabled(item);
      return (
        <DropdownMenuItem
          key={action.id}
          onSelect={() => onAction(action)}
          disabled={isDisabled}
          className={
            action.variant === "destructive" ? "text-red-600 focus:text-red-600" : ""
          }
        >
          <div className="flex items-center gap-2">
            {loadingActions.has(action.id) && (
              <Loader2 className="h-4 w-4 animate-spin" />
            )}
            {label}
          </div>
        </DropdownMenuItem>
      );
    },
    [item, loadingActions, onAction]
  );

  // Group the overflow actions by category so the "More" dropdown shows the
  // same labeled sections as the right-click menu. Destructive is pinned last
  // and renders WITHOUT a label (matching RowContextMenuContent / ActionDropdown).
  const secondaryGroups = React.useMemo(
    () => groupActionsByCategory(actionGroups.secondaryActions),
    [actionGroups.secondaryActions]
  );
  const secondaryDangerItems = React.useMemo(
    () =>
      secondaryGroups
        .filter((g) => g.category === "destructive")
        .flatMap((g) => g.items),
    [secondaryGroups]
  );
  const secondaryNonDangerGroups = React.useMemo(
    () => secondaryGroups.filter((g) => g.category !== "destructive"),
    [secondaryGroups]
  );

  // Single action — render directly without a dropdown
  if (visibleActions.length === 1) {
    return (
      <div className={`flex items-center gap-0.5 ${className}`}>
        {renderActionButton(visibleActions[0], false)}
      </div>
    );
  }

  // Multiple actions — primary as icons, secondary in overflow dropdown
  return (
    <div className={`flex items-center gap-0.5 ${className}`}>
      {actionGroups.primaryActions.map((action) => (
        <div key={action.id}>{renderActionButton(action, false)}</div>
      ))}

      {actionGroups.secondaryActions.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0 hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-center"
              title="More actions"
              aria-label="More actions"
            >
              <MoreHorizontal className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48 max-h-[400px] overflow-y-auto">
            {secondaryNonDangerGroups.map((group, groupIndex) => (
              <React.Fragment key={group.category}>
                {groupIndex > 0 && <DropdownMenuSeparator />}
                <DropdownMenuLabel className="px-2 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {group.label}
                </DropdownMenuLabel>
                {group.items.map(renderSecondaryItem)}
              </React.Fragment>
            ))}

            {secondaryDangerItems.length > 0 && (
              <>
                {secondaryNonDangerGroups.length > 0 && <DropdownMenuSeparator />}
                {secondaryDangerItems.map(renderSecondaryItem)}
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
